import type {EventsPrefillMorphData, EventTypeAiSearchParams} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import {MediaTypes} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import {capitalizeFirstLetterOfAllWords} from '@Utils/string.util';
import {openUserMediaModal} from '@Modals/user-media-modal';
import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import type {ImageData} from '@Libraries/add-media-library';
import {repoImageScreenURL} from '@Libraries/graphic-item-image-library';
import {onEventWizardCoverPhotoUpdate, onEventWizardIsCoverPhotoPreviewLoading} from '@Components/event-wizard/event-wizard-slice';
import {togglePreviewCoverPhotoLoading, updatePreviewCoverPhoto} from '@Components/event-wizard/preview/preview.helper';
import {EmbeddedEditorAction, openDesignInEmbeddedEditor} from '@Libraries/embedded-editor';
import type {PosterPageData} from '@Libraries/poster-library';
import {getPosterThumbnailImagePreviewURL, pollForPosterPreviewGenerationStatus} from '@Libraries/poster-library';
import {MorphType, PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import {noop} from '@Utils/general.util';
import {isPAYGUser} from '@Utils/user.util';
import {getGalleryUriFromEventAiSearchParams} from '@Libraries/events.library';
import {openErrorModal} from '@Modals/error-modal';
import {EVENT_ERROR_LOG_TAG} from '@Components/event-wizard/event-wizard.types';
import {openDesignSelector} from '@/email-maker/grapesjs-email-editor-plugin/src/helpers/design-selector-helper';

export const shouldUpsellAITemplateTagging = (doesPAYGUserHaveAITemplateLimit: boolean): boolean => {
  return isPAYGUser() ? !doesPAYGUserHaveAITemplateLimit : false;
};

export const getEventWizardMediaContainerTitle = (mediaType: MediaTypes): string => {
  let title = '';

  if (mediaType === MediaTypes.TEMPLATES) {
    title = window.i18next.t('pmwjs_ai_designs');
  }

  if (mediaType === MediaTypes.MY_DESIGNS) {
    title = window.i18next.t('pmwjs_my_designs');
  }

  if (mediaType === MediaTypes.UPLOAD) {
    title = window.i18next.t('pmwjs_uploads');
  }
  return capitalizeFirstLetterOfAllWords(title);
};

export const openUploadSelector = (): void => {
  openUserMediaModal({
    onAddSelectedCTAText: window.i18next.t('pmwjs_upload_image'),
    maxSelectionNumber: 1,
    defaultTab: UploadsMediaType.IMAGE,
    mediaTypesToShow: [UploadsMediaType.IMAGE],
    uploadAcceptedMimetype: 'image/*',
    onAddSelected: onSelectedFromUpload,
    hideRecordingOptions: true,
  });
};

export const openDesignSelectorEvents = (onSelected: () => void): void => {
  if (window.PMW.enableMyStuffMultipagesUI) {
    openDesignSelector(
      (pagesData: PosterPageData[]): void => {
        const firstKey = pagesData[0].posterId;
        const previewUrl = pagesData[0].previewUrl;
        onSelectedFromMyDesignSelector(firstKey, previewUrl);
        onSelected();
      },
      () => {
        window.PMW.setLoading(null);
      },
      false,
      false,
      false
    );
    return;
  }

  window.PMW.util.require(typeof window.PMW.openDesignSelectorModalLegacy === 'undefined', 'designSelector', true, () => {
    window.PMW.setLoading(null);
    window.PMW.openDesignSelectorModalLegacy(window.i18next.t('pmwjs_select_a_design'), (id: string, previewUrl?: string): void => {
      onSelectedFromMyDesignSelector(id, previewUrl);
      onSelected();
    });
  });
};

export const openGalleryTemplateSelector = (onSelected: () => void, aiSearchParams?: EventTypeAiSearchParams, opts?: EventsPrefillMorphData, shouldUpsell = false): void => {
  const galleryDesignClickedEvent = 'gallery-design-clicked',
    designGalleryPageUri = getGalleryUriFromEventAiSearchParams(aiSearchParams);

  window.addEventListener(galleryDesignClickedEvent, onSelectedFromGalleryTemplateSelector.bind(null, opts, onSelected, shouldUpsell) as EventListener);
  window.PMW.openGalleryDialog('', true, designGalleryPageUri, {showEventsWizardBanner: true, shouldUpsellForSemanticTagging: shouldUpsell}, false);
};

export const onRemoveMedia = (): void => {
  updateMedia(undefined, undefined);
  window.PMW.redux.store.dispatch(onEventWizardIsCoverPhotoPreviewLoading(false));
  togglePreviewCoverPhotoLoading(false);
};

// codeReviewShahrukhLater: refactor into a consolidated thing
export const openEmbeddedEditor = (hid: string, uploadedMediaType: MediaTypes, opts?: EventsPrefillMorphData, shouldUpsellAIPrefill = true): void => {
  const getParams = uploadedMediaType === MediaTypes.TEMPLATES && !shouldUpsellAIPrefill ? {...opts, morphType: MorphType.EVENTS_PREFILL} : {...opts};
  const mode = getEmbeddedEditorMode(uploadedMediaType, !shouldUpsellAIPrefill, opts);
  openDesignInEmbeddedEditor(
    hid,
    noop,
    (newHID: string) => {
      onEmbeddedEditorClosed(newHID, uploadedMediaType);
    },
    noop,
    mode,
    mode === EmbeddedEditorAction.MORPH ? window.i18next.t('pmwjs_done') : undefined,
    {getParams}
  );
};

export const isEmbeddedMorphModeActive = (): boolean => {
  return window.PMW.isEmbedded() && window.PMW.redux.store.getState().posterEditor.posterObject?.mode.details.type === PosterModeType.MORPH;
};

const updateMedia = (
  url: string | undefined,
  uploadedMediaType: MediaTypes | undefined,
  selectedTemplateHID: string | undefined = undefined,
  uploadMediaHID: string | undefined = undefined
): void => {
  window.PMW.redux.store.dispatch(
    onEventWizardCoverPhotoUpdate({
      uploadedUrl: url,
      uploadMediaHID: uploadMediaHID,
      mediaType: uploadedMediaType,
      selectedTemplateHID,
    })
  );
  void updatePreviewCoverPhoto(url);
};

const onSelectedFromGalleryTemplateSelector = (
  opts: EventsPrefillMorphData | undefined,
  onSelected: () => void,
  shouldUpsellAIPrefill = true,
  event: CustomEvent<{id: string}>
): void => {
  openEmbeddedEditor(event.detail.id, MediaTypes.TEMPLATES, opts, shouldUpsellAIPrefill);
  onSelected();
  window.PMW.closeGalleryDialog(window.location.href, false);
};

const onSelectedFromMyDesignSelector = (hid: string, previewUrl?: string): void => {
  updateMedia(previewUrl, MediaTypes.MY_DESIGNS, hid);
};

const getEmbeddedEditorMode = (uploadedMediaType: MediaTypes, isPremium: boolean, opts?: EventsPrefillMorphData): EmbeddedEditorAction => {
  switch (uploadedMediaType) {
    case MediaTypes.MY_DESIGNS:
      return EmbeddedEditorAction.EDIT;
    case MediaTypes.TEMPLATES:
      return isPremium && opts && Object.keys(opts).length > 0 ? EmbeddedEditorAction.MORPH : EmbeddedEditorAction.COPY;
    default:
      throw new Error(`Unhandled case for given media type: ${uploadedMediaType}`);
  }
};

const isPolledHIDStillCurrent = (hid: string): boolean => {
  return window.PMW.redux.store.getState().eventWizard.coverPhoto.selectedTemplateHID === hid;
};

const preloadCoverPhotoImage = (url: string): Promise<void> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = (): void => {
      resolve();
    };
    img.onerror = (): void => {
      resolve();
    };
    img.src = url;
  });
};

const onEmbeddedEditorClosed = (hid: string, uploadedMediaType: MediaTypes | undefined): void => {
  const currentCoverPhoto = window.PMW.redux.store.getState().eventWizard.coverPhoto;
  window.PMW.redux.store.dispatch(
    onEventWizardCoverPhotoUpdate({
      ...currentCoverPhoto,
      mediaType: uploadedMediaType,
      selectedTemplateHID: hid,
    })
  );
  window.PMW.redux.store.dispatch(onEventWizardIsCoverPhotoPreviewLoading(true));
  togglePreviewCoverPhotoLoading(true);

  const handleCoverPhotoPollError = (err: unknown): void => {
    if (!isPolledHIDStillCurrent(hid)) {
      return;
    }
    updateMedia(undefined, undefined, undefined);
    window.PMW.redux.store.dispatch(onEventWizardIsCoverPhotoPreviewLoading(false));
    const errMsg = err instanceof Error ? err.message : '';
    openErrorModal({
      message: window.i18next.t('pmwjs_error_message'),
      log: true,
      debugDetails: errMsg + '. ' + EVENT_ERROR_LOG_TAG,
    });
  };

  pollForPosterPreviewGenerationStatus(
    hid,
    async () => {
      try {
        if (!isPolledHIDStillCurrent(hid)) {
          return;
        }
        const previewUrl = await getPosterThumbnailImagePreviewURL(hid);
        if (!isPolledHIDStillCurrent(hid)) {
          return;
        }
        await preloadCoverPhotoImage(previewUrl);
        if (!isPolledHIDStillCurrent(hid)) {
          return;
        }
        updateMedia(previewUrl, uploadedMediaType, hid);
        window.PMW.redux.store.dispatch(onEventWizardIsCoverPhotoPreviewLoading(false));
      } catch (err) {
        handleCoverPhotoPollError(err);
      }
    },
    handleCoverPhotoPollError
  );
};

const onSelectedFromUpload = (images: ImageData[], currentUploadIndex?: number): void => {
  if (currentUploadIndex && currentUploadIndex > 1) {
    return;
  }
  const url = repoImageScreenURL(images[0].hashedFilename, images[0].extension);
  updateMedia(url, MediaTypes.UPLOAD, undefined, images[0].hashedFilename);
};
